import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EnabledExportPages,
  EnabledExportsMap,
  ExportModalSettings,
  ExportTabView,
  UseExportModalReturnType,
  ViewType,
} from '../Model';
import { ENABLED_EXPORTS_MAP } from './ExportModalConfig';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import { displayExportErrorToastMessage } from '../ExportUtils';

interface UseExportModalProps {
  currentPage: EnabledExportPages;
  currentTab: ExportTabView;
  currentView: ViewType;
  currentCountryAndProduct: string;
}

export const useExportModal = (useExportModalProps: UseExportModalProps): UseExportModalReturnType => {
  const { currentPage, currentTab, currentView, currentCountryAndProduct } = useExportModalProps;

  const isExportEnabled = useMemo(() => {
    const modalSettings = findExportModalSetting(currentPage, currentTab, currentView, currentCountryAndProduct);

    return (modalSettings?.exportFormats?.length ?? 0) > 0;
  }, [currentPage, currentTab, currentView, currentCountryAndProduct]);

  const exportModalSettings = useMemo<ExportModalSettings>(() => {
    const modalSettings = findExportModalSetting(currentPage, currentTab, currentView, currentCountryAndProduct);

    return (
      modalSettings || {
        exportFormats: [],
        modalMessage: '',
        modalHeaderText: '',
      }
    );
  }, [currentPage, currentTab, currentView, currentCountryAndProduct]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const openExportModal = useCallback(() => {
    if (isMounted.current) {
      setIsModalOpen(true);
    }
  }, []);

  const onCloseExportModal = useCallback(() => {
    if (isMounted.current) {
      setIsModalOpen(false);
    }
  }, []);

  const { showToastMessage } = useToastMessage();
  const onExportError = useCallback(
    (error: Error) => {
      const requestPayload = {
        currentPage: currentPage,
        currentTab: currentTab,
        currentView: currentView,
      };
      const errorMessage = `Export Modal - ${error.message}.`;
      displayExportErrorToastMessage(error, errorMessage, requestPayload, showToastMessage);
    },
    [showToastMessage],
  );
  return {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats: exportModalSettings.exportFormats,
    exportModalHeaderText: exportModalSettings.modalHeaderText,
    exportModalMessage: exportModalSettings.modalMessage,
    isExportModalOpen: isModalOpen,
    isExportEnabled,
  };
};

const findExportModalSetting = (
  currentPage: EnabledExportPages,
  currentTab: ExportTabView,
  currentView: ViewType,
  currentCountryAndProduct: string,
  exportMap: EnabledExportsMap = ENABLED_EXPORTS_MAP,
): ExportModalSettings | undefined => {
  // @ts-ignore
  const baseSetting = exportMap[currentPage]?.[currentTab as string]?.[currentView];

  // @ts-ignore
  return baseSetting?.exportModalSettingsOverrides?.[currentCountryAndProduct] ?? baseSetting;
};
